import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function Questionnaire() {
  const { framework } = useParams();
  const navigate = useNavigate();
  const [structure, setStructure] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [section, setSection] = useState(0);
  const [error, setError] = useState("");
  const [evaluating, setEvaluating] = useState(false);
  const dirtyRef = useRef(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/questionnaire/${framework}`);
      setStructure(data.structure);
      setQuestionnaire(data.questionnaire);
      setAnswers(data.questionnaire.answers || {});
    })();
  }, [framework]);

  const save = useCallback(async (a) => {
    try { await api.put(`/questionnaire/${framework}`, { answers: a }); dirtyRef.current = false; } catch {}
  }, [framework]);

  // Auto-save cada 10s
  useEffect(() => {
    const t = setInterval(() => { if (dirtyRef.current) save(answers); }, 10000);
    return () => clearInterval(t);
  }, [answers, save]);

  const setAnswer = (sid, qid, val) => {
    dirtyRef.current = true;
    setAnswers((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), [qid]: val } }));
  };

  if (!structure) return <div className="text-slate-500">Cargando cuestionario...</div>;

  const sections = structure.sections;
  const current = sections[section];

  const validateCurrent = () => {
    for (const q of current.questions) {
      if (!q.required) continue;
      const v = (answers[current.id] || {})[q.id];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0))
        return `Falta responder "${q.text}".`;
    }
    return "";
  };

  const next = async () => {
    const err = validateCurrent();
    if (err) { setError(err); return; }
    setError("");
    await save(answers);
    setSection((s) => Math.min(sections.length - 1, s + 1));
  };

  const back = async () => { await save(answers); setSection((s) => Math.max(0, s - 1)); };

  const exitSave = async () => { await save(answers); toast.success("Cuestionario guardado."); navigate("/dashboard"); };

  const complete = async () => {
    const err = validateCurrent();
    if (err) { setError(err); return; }
    await save(answers);
    setError("");
    setEvaluating(true);
    try {
      await api.post(`/questionnaire/${framework}/complete`);
      const start = Date.now();
      const poll = async () => {
        if (Date.now() - start > 90000) {
          toast.error("La evaluación está tardando más de lo habitual. Puedes revisar el SoA manualmente.");
          navigate(`/soa?framework=${framework}`);
          return;
        }
        const { data } = await api.get(`/questionnaire/${framework}`);
        const st = data.questionnaire.llm_evaluation_status;
        if (st === "completed") {
          toast.success("Aplicabilidad evaluada por IA. SoA listo.");
          navigate(`/soa?framework=${framework}`);
        } else if (st === "failed") {
          toast.warning("La evaluación automática ha fallado. Todos los controles se han marcado como aplicables; revísalos manualmente.");
          navigate(`/soa?framework=${framework}`);
        } else {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      setEvaluating(false);
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  if (evaluating) {
    return (
      <div data-testid="evaluating" className="flex flex-col items-center justify-center py-20 text-slate-700">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div className="mt-4 text-[14px]">Evaluando aplicabilidad con IA...</div>
        <div className="text-[12px] text-slate-500 mt-1">Esto puede tardar hasta 90 segundos.</div>
      </div>
    );
  }

  return (
    <div data-testid="questionnaire-page" className="max-w-3xl">
      <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Cuestionario inicial — ISO 27001</h1>
      <div className="flex items-center gap-2 mt-4">
        {sections.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 flex-1">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-semibold ${i < section ? "bg-primary text-white" : i === section ? "bg-primary/10 text-primary border border-primary" : "bg-slate-100 text-slate-400"}`}>{i + 1}</div>
            <span className={`text-[12px] ${i === section ? "text-slate-900" : "text-slate-500"}`}>{s.title}</span>
            {i < sections.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-1" />}
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-6 space-y-5">
        <h2 className="text-[16px] font-semibold text-slate-900">{current.title}</h2>
        {current.questions.map((q) => (
          <Question key={q.id} q={q} value={(answers[current.id] || {})[q.id]} onChange={(v) => setAnswer(current.id, q.id, v)} />
        ))}
        {error && <div data-testid="quest-error" className="text-[12px] text-destructive">{error}</div>}
      </div>

      <div className="flex items-center justify-between mt-4">
        <Button variant="ghost" onClick={back} disabled={section === 0} data-testid="quest-back"><ChevronLeft className="h-4 w-4 mr-1" /> Anterior</Button>
        <Button variant="outline" onClick={exitSave} data-testid="quest-save-exit">Guardar y salir</Button>
        {section < sections.length - 1 ? (
          <Button onClick={next} data-testid="quest-next">Siguiente <ChevronRight className="h-4 w-4 ml-1" /></Button>
        ) : (
          <Button onClick={complete} data-testid="quest-complete">Completar cuestionario</Button>
        )}
      </div>
    </div>
  );
}

function Question({ q, value, onChange }) {
  return (
    <div data-testid={`q-${q.id}`}>
      <Label className="text-[14px] text-slate-900">{q.text}{q.required && <span className="text-destructive ml-1">*</span>}</Label>
      <div className="mt-2">
        {q.type === "text" && (
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />
        )}
        {q.type === "yes_no" && (
          <RadioGroup value={value || ""} onValueChange={onChange} className="flex gap-4">
            <div className="flex items-center gap-2"><RadioGroupItem value="yes" id={`${q.id}-y`} /><Label htmlFor={`${q.id}-y`}>Sí</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="no" id={`${q.id}-n`} /><Label htmlFor={`${q.id}-n`}>No</Label></div>
          </RadioGroup>
        )}
        {q.type === "single_select" && (
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger><SelectValue placeholder="Selecciona..." /></SelectTrigger>
            <SelectContent>{q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {q.type === "multi_select" && (
          <div className="grid grid-cols-2 gap-2">
            {q.options.map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.includes(o);
              return (
                <label key={o} className="flex items-center gap-2 text-[14px]">
                  <Checkbox checked={checked} onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((x) => x !== o))} />
                  {o}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
